import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Check, X, Key } from "@phosphor-icons/react";

const ROLES = ["owner", "spouse", "accountant"];

function roleLabel(r) {
    if (r === "owner") return "Owner (full access)";
    if (r === "spouse") return "Spouse (view + edit)";
    if (r === "accountant") return "Accountant (view + export)";
    return r;
}

export default function Users() {
    const { user: current } = useAuth();
    const [users, setUsers] = useState([]);
    const [editing, setEditing] = useState(null);
    const [adding, setAdding] = useState(null);
    const [resetting, setResetting] = useState(null); // { id, password }
    const [busy, setBusy] = useState(false);

    const isOwner = current?.role === "owner";

    const load = async () => {
        try { setUsers((await api.get("/users")).data); }
        catch { toast.error("Failed to load users (owner-only)"); }
    };
    useEffect(() => { if (isOwner) load(); }, [isOwner]);

    const startAdd = () => setAdding({ email: "", name: "", role: "spouse", password: "" });
    const cancelAdd = () => setAdding(null);
    const saveAdd = async () => {
        if (!adding.email || !adding.name || !adding.password) {
            toast.warning("Fill all fields"); return;
        }
        setBusy(true);
        try {
            await api.post("/users", adding);
            toast.success("User created");
            setAdding(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to create user");
        } finally { setBusy(false); }
    };

    const startEdit = (u) => setEditing({ id: u.id, name: u.name, role: u.role });
    const cancelEdit = () => setEditing(null);
    const saveEdit = async () => {
        setBusy(true);
        try {
            await api.put(`/users/${editing.id}`, {
                name: editing.name,
                role: editing.role,
            });
            toast.success("User updated");
            setEditing(null);
            await load();
        } catch { toast.error("Failed to update"); }
        finally { setBusy(false); }
    };

    const del = async (u) => {
        if (!confirm(`Delete user ${u.name}? This can't be undone.`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("User deleted");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to delete");
        }
    };

    const savePassword = async () => {
        if (!resetting?.password || resetting.password.length < 6) {
            toast.warning("Password must be 6+ characters"); return;
        }
        setBusy(true);
        try {
            await api.post(`/users/${resetting.id}/reset-password`, {
                new_password: resetting.password,
            });
            toast.success("Password reset");
            setResetting(null);
        } catch { toast.error("Failed to reset password"); }
        finally { setBusy(false); }
    };

    if (!isOwner) {
        return (
            <div className="hairline bg-white p-8 rounded text-sm"
                 style={{ color: "var(--muted)" }}
                 data-testid="users-not-authorised">
                User administration is available only to owner accounts.
            </div>
        );
    }

    return (
        <div data-testid="users-page">
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">User Administration</div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">
                        Who has access.
                    </h1>
                    <p className="text-sm mt-3 max-w-2xl" style={{ color: "var(--muted)" }}>
                        Owner accounts have full access. Spouse can view and edit but not
                        export or manage users. Accountant can view and export.
                    </p>
                </div>
                <button className="btn-primary text-sm flex items-center gap-2"
                        onClick={startAdd} disabled={adding !== null}
                        data-testid="user-add-btn">
                    <Plus size={14} /> Add user
                </button>
            </div>

            <div className="hairline bg-white rounded overflow-hidden">
                <table className="editorial w-full" data-testid="users-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th style={{ width: 140 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {adding && (
                            <tr className="bg-[var(--eucalyptus)]/10" data-testid="user-add-row">
                                <td>
                                    <input
                                        value={adding.name}
                                        onChange={e => setAdding(a => ({ ...a, name: e.target.value }))}
                                        placeholder="Full name"
                                        data-testid="user-add-name"
                                        className="border border-[var(--line)] px-2 py-1 rounded text-sm w-full" />
                                </td>
                                <td>
                                    <input
                                        type="email"
                                        value={adding.email}
                                        onChange={e => setAdding(a => ({ ...a, email: e.target.value }))}
                                        placeholder="email@example.com"
                                        data-testid="user-add-email"
                                        className="border border-[var(--line)] px-2 py-1 rounded text-sm w-full" />
                                </td>
                                <td>
                                    <select
                                        value={adding.role}
                                        onChange={e => setAdding(a => ({ ...a, role: e.target.value }))}
                                        data-testid="user-add-role"
                                        className="border border-[var(--line)] px-2 py-1 rounded text-sm w-full">
                                        {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                                    </select>
                                </td>
                                <td>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="password"
                                            value={adding.password}
                                            onChange={e => setAdding(a => ({ ...a, password: e.target.value }))}
                                            placeholder="Password"
                                            data-testid="user-add-password"
                                            className="border border-[var(--line)] px-2 py-1 rounded text-xs w-24" />
                                        <button onClick={saveAdd} disabled={busy}
                                                className="text-[var(--eucalyptus)] hover:opacity-70"
                                                data-testid="user-add-save">
                                            <Check size={16} />
                                        </button>
                                        <button onClick={cancelAdd}
                                                className="text-[var(--muted)] hover:text-[var(--terracotta)]">
                                            <X size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {users.map(u => {
                            const isEditing = editing?.id === u.id;
                            const isResetting = resetting?.id === u.id;
                            const isSelf = u.id === current?.id;
                            return (
                                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                    <td className="font-medium">
                                        {isEditing ? (
                                            <input value={editing.name}
                                                   onChange={e => setEditing(x => ({ ...x, name: e.target.value }))}
                                                   className="border border-[var(--line)] px-2 py-1 rounded text-sm w-full"
                                                   data-testid={`user-edit-name-${u.id}`} />
                                        ) : u.name}
                                        {isSelf && (
                                            <span className="ml-2 text-[10px] uppercase tracking-widest text-[var(--muted)]">
                                                you
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-sm text-[var(--muted)]">{u.email}</td>
                                    <td className="text-sm">
                                        {isEditing ? (
                                            <select value={editing.role}
                                                    onChange={e => setEditing(x => ({ ...x, role: e.target.value }))}
                                                    data-testid={`user-edit-role-${u.id}`}
                                                    className="border border-[var(--line)] px-2 py-1 rounded text-sm w-full">
                                                {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                                            </select>
                                        ) : roleLabel(u.role)}
                                    </td>
                                    <td>
                                        {isResetting ? (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="password"
                                                    value={resetting.password}
                                                    onChange={e => setResetting(r => ({ ...r, password: e.target.value }))}
                                                    placeholder="New password"
                                                    data-testid={`user-pw-input-${u.id}`}
                                                    className="border border-[var(--line)] px-2 py-1 rounded text-xs w-32" />
                                                <button onClick={savePassword} disabled={busy}
                                                        className="text-[var(--eucalyptus)] hover:opacity-70"
                                                        data-testid={`user-pw-save-${u.id}`}>
                                                    <Check size={16} />
                                                </button>
                                                <button onClick={() => setResetting(null)}
                                                        className="text-[var(--muted)] hover:text-[var(--terracotta)]">
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ) : isEditing ? (
                                            <div className="flex items-center gap-2">
                                                <button onClick={saveEdit} disabled={busy}
                                                        className="text-[var(--eucalyptus)] hover:opacity-70"
                                                        data-testid={`user-edit-save-${u.id}`}>
                                                    <Check size={16} />
                                                </button>
                                                <button onClick={cancelEdit}
                                                        className="text-[var(--muted)] hover:text-[var(--terracotta)]">
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => startEdit(u)}
                                                        className="text-[var(--muted)] hover:text-[var(--ink)]"
                                                        title="Edit"
                                                        data-testid={`user-edit-${u.id}`}>
                                                    <PencilSimple size={14} />
                                                </button>
                                                <button onClick={() => setResetting({ id: u.id, password: "" })}
                                                        className="text-[var(--muted)] hover:text-[var(--ink)]"
                                                        title="Reset password"
                                                        data-testid={`user-pw-${u.id}`}>
                                                    <Key size={14} />
                                                </button>
                                                {!isSelf && (
                                                    <button onClick={() => del(u)}
                                                            className="text-[var(--muted)] hover:text-[var(--terracotta)]"
                                                            title="Delete"
                                                            data-testid={`user-del-${u.id}`}>
                                                        <Trash size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {users.length === 0 && !adding && (
                            <tr><td colSpan={4} className="p-8 text-center text-sm text-[var(--muted)]">
                                No users. Click Add user to create one.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
